import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Shield, User, UserCog, Settings } from 'lucide-react';
import logger from '@/utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard', description: 'View dashboard overview and stats' },
  { key: 'packages', label: 'Tour Packages', description: 'Manage tour packages and destinations' },
  { key: 'theme_tours', label: 'Theme Tours', description: 'Manage theme tours and categories' },
  { key: 'leads', label: 'Leads & Enquiries', description: 'Lead pipeline, enquiries, contacts, wedding, corporate' },
  { key: 'content', label: 'Content', description: 'FAQs, Blog, Reviews, Site Pages' },
  { key: 'promotions', label: 'Promotions', description: 'Exit-intent popups and countdown banners' },
  { key: 'meta_ads', label: 'Meta Ads', description: 'Meta Ads lead sync integration' },
  { key: 'reports', label: 'Reports', description: 'View reports and analytics' },
  { key: 'security', label: 'Security', description: 'Security settings and login audit' },
  { key: 'users', label: 'User Management', description: 'Manage admin users (super admin only)' },
];

const AdminUsers = () => {
  const { getAuthHeaders, admin: currentAdmin } = useAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showModal, setShowModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    is_active: true,
    permissions: AVAILABLE_PERMISSIONS.map(p => p.key)
  });

  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      logger.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'admin',
      is_active: true,
      permissions: AVAILABLE_PERMISSIONS.map(p => p.key)
    });
    setEditingUser(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      is_active: user.is_active,
      permissions: user.permissions || AVAILABLE_PERMISSIONS.map(p => p.key)
    });
    setShowModal(true);
  };

  const openPermissionsModal = (user) => {
    setPermissionsUser(user);
    setSelectedPermissions(user.permissions || AVAILABLE_PERMISSIONS.map(p => p.key));
    setShowPermissionsModal(true);
  };

  const handlePermissionToggle = (permissionKey) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionKey)) {
        return prev.filter(p => p !== permissionKey);
      } else {
        return [...prev, permissionKey];
      }
    });
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${permissionsUser.id}`,
        { permissions: selectedPermissions },
        { headers: getAuthHeaders() }
      );
      toast.success('Permissions updated successfully');
      setShowPermissionsModal(false);
      setPermissionsUser(null);
      fetchUsers();
    } catch (error) {
      logger.error('Error updating permissions:', error);
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        const payload = {
          name: formData.name,
          email: formData.email,
          is_active: formData.is_active
        };
        if (formData.password) {
          payload.password = formData.password;
        }
        
        await axios.put(
          `${API_URL}/api/admin/users/${editingUser.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('User updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/admin/users`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('User created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      logger.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });
      toast.success('User deleted successfully');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      logger.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="admin-users">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-800">User Management</h1>
          <p className="text-sm lg:text-base text-slate-500">Manage admin users and permissions</p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      {/* Users Table/Cards */}
      <Card>
        <CardContent className="p-0">
          {/* Mobile Cards View */}
          <div className="lg:hidden divide-y">
            {users.map((user) => (
              <div key={user.id} className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    user.role === 'super_admin' ? 'bg-purple-100' : 'bg-blue-100'
                  }`}>
                    {user.role === 'super_admin' ? (
                      <Shield className="w-5 h-5 text-purple-600" />
                    ) : (
                      <User className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{user.name}</p>
                    <p className="text-sm text-slate-500 truncate">{user.email}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.role === 'super_admin' 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {user.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {user.role !== 'super_admin' && (
                    <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">
                      {(user.permissions || []).length} permissions
                    </span>
                  )}
                </div>
                {user.id !== currentAdmin?.id && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(user)}
                      className="flex-1"
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Pencil size={14} className="mr-1" /> Edit
                    </Button>
                    {user.role !== 'super_admin' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPermissionsModal(user)}
                        className="flex-1 text-amber-600"
                        data-testid={`permissions-user-${user.id}`}
                      >
                        <Settings size={14} className="mr-1" /> Permissions
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteConfirm(user)}
                      className="text-red-600"
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                )}
                {user.id === currentAdmin?.id && (
                  <p className="text-xs text-slate-400 pt-2">Current User</p>
                )}
              </div>
            ))}
            {users.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <UserCog className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No users found</p>
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="text-left text-sm text-slate-600">
                  <th className="p-4 font-semibold">User</th>
                  <th className="p-4 font-semibold">Email</th>
                  <th className="p-4 font-semibold">Role</th>
                  <th className="p-4 font-semibold">Permissions</th>
                  <th className="p-4 font-semibold">Status</th>
                  <th className="p-4 font-semibold">Last Login</th>
                  <th className="p-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-slate-50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.role === 'super_admin' ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          {user.role === 'super_admin' ? (
                            <Shield className="w-5 h-5 text-purple-600" />
                          ) : (
                            <User className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <span className="font-medium text-slate-800">{user.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{user.email}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.role === 'super_admin' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </span>
                    </td>
                    <td className="p-4">
                      {user.role === 'super_admin' ? (
                        <span className="text-xs text-slate-500">All Access</span>
                      ) : (
                        <span className="text-xs text-slate-500">
                          {(user.permissions || []).length} of {AVAILABLE_PERMISSIONS.length}
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-500">
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleDateString() 
                        : 'Never'}
                    </td>
                    <td className="p-4">
                      {user.id !== currentAdmin?.id && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditModal(user)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Edit User"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          {user.role !== 'super_admin' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openPermissionsModal(user)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Manage Permissions"
                              data-testid={`permissions-user-${user.id}`}
                            >
                              <Settings size={16} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirm(user)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete User"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      )}
                      {user.id === currentAdmin?.id && (
                        <span className="text-xs text-slate-400">Current User</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <UserCog className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No users found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit User' : 'Create New User'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="input-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="input-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                Password {editingUser ? '(leave blank to keep current)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                data-testid="input-password"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="input-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Super Admins have all permissions. Admin permissions can be customized after creation.
                </p>
              </div>
            )}

            {editingUser && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                  data-testid="input-active"
                />
                <Label htmlFor="is_active">Active Account</Label>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-user-btn">
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions Modal */}
      <Dialog open={showPermissionsModal} onOpenChange={setShowPermissionsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Manage Permissions for {permissionsUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Select which features this user can access in the admin dashboard.
            </p>
            <div className="space-y-3">
              {AVAILABLE_PERMISSIONS.map((permission) => (
                <div
                  key={permission.key}
                  className="flex items-start gap-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => handlePermissionToggle(permission.key)}
                >
                  <Checkbox
                    checked={selectedPermissions.includes(permission.key)}
                    onCheckedChange={() => handlePermissionToggle(permission.key)}
                    data-testid={`permission-${permission.key}`}
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{permission.label}</p>
                    <p className="text-xs text-slate-500">{permission.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPermissionsModal(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSavePermissions} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="save-permissions-btn"
            >
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(deleteConfirm?.id)}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
